import Hls from "hls.js";

/**
 * HLS playback for one video element, with the frame clock the review needs.
 * hls.js over MSE wherever MSE exists (Chrome, Firefox, desktop and iPad
 * Safari); the browser's own HLS only where it does not (iPhone Safari).
 * Chrome on Windows answers "maybe" to native HLS and then seeks to segment
 * edges and dies on a 403, so native is never preferred. Either way a seek is
 * `currentTime = t`, never fastSeek, so it lands on the frame that contains t.
 * The frame on screen is read from requestVideoFrameCallback where the browser
 * has it, so a moment is stored at the presented frame's own time and a step
 * moves exactly one frame.
 */

export interface LoadOptions {
    startAt: number;
    autoplay: boolean;
}

export interface Player {
    /** Play a playlist from `startAt`. Reloading the same clip with a fresh signature keeps the position. */
    load(src: string, opts: LoadOptions): void;
    /** Seek to an exact time. */
    seek(t: number): void;
    /** Move a whole number of frames from the frame on screen, pausing first. */
    step(frames: number): void;
    /** The time to store for the frame on screen: the middle of that frame, so any player lands on it. */
    frameTime(): number;
    /** Seconds per frame, measured from presented frames; the fleet's 25 fps until measured. */
    frameDuration(): number;
    destroy(): void;
}

const DEFAULT_FRAME_SEC = 1 / 25;

type FrameCallbackVideo = HTMLVideoElement & {
    requestVideoFrameCallback?: (cb: (now: number, meta: { mediaTime: number }) => void) => number;
    cancelVideoFrameCallback?: (handle: number) => void;
};

export function supportsNativeHls(video: HTMLVideoElement): boolean {
    return video.canPlayType("application/vnd.apple.mpegurl") !== "";
}

/** hls.js wherever it can run; the browser's own HLS only as the fallback. */
export function engineFor(video: HTMLVideoElement): "hls.js" | "native" | "none" {
    if (Hls.isSupported()) return "hls.js";
    return supportsNativeHls(video) ? "native" : "none";
}

export function createPlayer(video: HTMLVideoElement, onNetworkError: () => void): Player {
    const v = video as FrameCallbackVideo;
    const engine = engineFor(video);
    let hls: Hls | null = null;
    let presented: number | null = null;
    let frameSec = DEFAULT_FRAME_SEC;
    const deltas: number[] = [];
    let rvfc = 0;

    const onFrame = (_now: number, meta: { mediaTime: number }) => {
        if (presented !== null && !video.paused) {
            const d = meta.mediaTime - presented;
            // Only steady playback measures the frame; seeks and stalls are not frames.
            if (d > 0.004 && d < 0.1) {
                deltas.push(d);
                if (deltas.length > 48) deltas.shift();
                if (deltas.length >= 8) {
                    const sorted = [...deltas].sort((a, b) => a - b);
                    frameSec = sorted[Math.floor(sorted.length / 2)];
                }
            }
        }
        presented = meta.mediaTime;
        rvfc = v.requestVideoFrameCallback ? v.requestVideoFrameCallback(onFrame) : 0;
    };
    if (v.requestVideoFrameCallback) rvfc = v.requestVideoFrameCallback(onFrame);

    const detach = () => {
        if (hls) { hls.destroy(); hls = null; }
        presented = null;
    };

    // Players differ by a millisecond or two in where a frame begins, so a moment is
    // stored at the middle of its frame, where every one of them shows the same frame.
    const frameTime = (): number => {
        const t = video.currentTime;
        if (presented !== null && t >= presented - 0.0005 && t < presented + frameSec * 1.5) return presented + frameSec / 2;
        return t;
    };

    return {
        load(src, { startAt, autoplay }) {
            detach();
            const begin = () => {
                if (startAt > 0) video.currentTime = startAt;
                if (autoplay) void video.play().catch(() => undefined);
            };
            if (engine === "native") {
                video.src = src;
                video.addEventListener("loadedmetadata", begin, { once: true });
                video.load();
                return;
            }
            if (engine === "none") { onNetworkError(); return; }
            hls = new Hls({
                startPosition: startAt > 0 ? startAt : -1,
                maxBufferLength: 20,
                backBufferLength: 10,
                enableWorker: true,
                fragLoadingMaxRetry: 2,
                manifestLoadingMaxRetry: 2,
                levelLoadingMaxRetry: 2,
            });
            hls.on(Hls.Events.ERROR, (_event, data) => {
                if (!data.fatal) return;
                if (data.type === Hls.ErrorTypes.NETWORK_ERROR) { onNetworkError(); return; }
                if (data.type === Hls.ErrorTypes.MEDIA_ERROR) { hls?.recoverMediaError(); return; }
                onNetworkError();
            });
            hls.on(Hls.Events.MANIFEST_PARSED, () => { if (autoplay) void video.play().catch(() => undefined); });
            hls.loadSource(src);
            hls.attachMedia(video);
            if (import.meta.env.DEV) (window as unknown as { __hls?: Hls }).__hls = hls;
        },
        seek(t) {
            video.currentTime = Math.max(0, t);
        },
        // Firefox reports the playhead, not the frame's own time, as the presented
        // time, so a step is taken on the frame grid: whichever frame holds the base
        // time, land in the middle of the one that many frames away from it.
        step(frames) {
            video.pause();
            const base = presented !== null && Math.abs(video.currentTime - presented) < frameSec * 1.5 ? presented : video.currentTime;
            const index = Math.floor(base / frameSec + 1e-6);
            video.currentTime = Math.max(0, (index + frames + 0.5) * frameSec);
        },
        frameTime,
        frameDuration: () => frameSec,
        destroy() {
            if (rvfc && v.cancelVideoFrameCallback) v.cancelVideoFrameCallback(rvfc);
            detach();
            video.removeAttribute("src");
            video.load();
        },
    };
}
